// @ts-check
/** @typedef {import('../types/api').Dress} Dress */
/** @typedef {import('../types/api').HubManager} HubManager */
/** @typedef {import('../types/api').Influencer} Influencer */
/** @typedef {import('../types/api').Product} Product */

import { ChevronRight, MapPin, Package, Plus, RefreshCw, Search, Truck, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import Modal, { ModalDrawer } from '../components/ui/Modal';
import { useToast } from '../context/ToastContext';
import adminProductService from '../services/adminProductService';
import dressService from '../services/dressService';
import influencerService from '../services/influencerService';

// ─── Status Maps ──────────────────────────────────────────────────────────────

const CIRCULATION_STATUS = {
  with_hub: { label: 'With Hub', bg: 'bg-muted', text: 'text-foreground/80' },
  preparing: { label: 'Preparing', bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-700 dark:text-blue-300' },
  with_warehouse: {
    label: 'With Warehouse',
    bg: 'bg-indigo-100 dark:bg-indigo-900/40',
    text: 'text-indigo-700 dark:text-indigo-300',
  },
  shipped_to_influencer: {
    label: 'Shipped to Influencer',
    bg: 'bg-purple-100 dark:bg-purple-900/40',
    text: 'text-purple-700 dark:text-purple-300',
  },
  with_influencer: {
    label: 'With Influencer',
    bg: 'bg-green-100 dark:bg-green-900/40',
    text: 'text-green-700 dark:text-green-300',
  },
  return_shipped: {
    label: 'Return Shipped',
    bg: 'bg-yellow-100 dark:bg-yellow-900/40',
    text: 'text-yellow-700 dark:text-yellow-300',
  },
  with_cleaners: {
    label: 'With Cleaners',
    bg: 'bg-orange-100 dark:bg-orange-900/40',
    text: 'text-orange-700 dark:text-orange-300',
  },
  with_repackagers: {
    label: 'With Repackagers',
    bg: 'bg-pink-100 dark:bg-pink-900/40',
    text: 'text-pink-700 dark:text-pink-300',
  },
  returned_to_hub: { label: 'Returned to Hub', bg: 'bg-muted', text: 'text-foreground/80' },
  retired: { label: 'Retired', bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300' },
};

const StatusBadge = ({ value }) => {
  const info = CIRCULATION_STATUS[value] || { label: value, bg: 'bg-muted', text: 'text-foreground/80' };
  return <span className={`px-2 py-1 text-xs rounded-full font-medium ${info.bg} ${info.text}`}>{info.label}</span>;
};

const daysBetween = (dateStr) => {
  if (!dateStr) return '\u2014';
  const diff = Math.floor((new Date().getTime() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
  return diff >= 0 ? diff : 0;
};

// Map status to the relevant due-date field name
const getDueDateField = (status) => {
  const map = {
    with_warehouse: 'due_at_warehouse',
    shipped_to_influencer: 'due_at_influencer',
    with_influencer: 'due_at_influencer',
    with_cleaners: 'due_at_cleaners',
    with_repackagers: 'due_at_repackagers',
    return_shipped: 'due_at_hub',
  };
  return map[status] || null;
};

const DueDateLabel = ({ shipment }) => {
  const field = getDueDateField(shipment.status);
  const dateStr = field ? shipment[field] : null;
  if (!dateStr) return <span className="text-muted-foreground/70">{'\u2014'}</span>;
  const diff = Math.ceil((new Date(dateStr).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
  if (diff > 0)
    return (
      <span className="text-foreground/80">
        {diff} day{diff !== 1 ? 's' : ''} left
      </span>
    );
  if (diff === 0) return <span className="text-yellow-600 dark:text-yellow-400 font-medium">Due today</span>;
  return (
    <span className="text-red-600 dark:text-red-400 font-medium">
      Overdue by {Math.abs(diff)} day{Math.abs(diff) !== 1 ? 's' : ''}
    </span>
  );
};

const getThirdPartyName = (shipment) => {
  if (['shipped_to_influencer', 'return_shipped'].includes(shipment.status)) return shipment.shipping_company;
  if (shipment.status === 'with_warehouse') return shipment.warehouse_name;
  if (shipment.status === 'with_cleaners') return shipment.cleaners_name;
  if (shipment.status === 'with_repackagers') return shipment.repackagers_name;
  return null;
};

// Fields required for each status transition
const STATUS_EXTRA_FIELDS = {
  shipped_to_influencer: ['shipping_company', 'tracking_number'],
  return_shipped: ['shipping_company', 'tracking_number'],
  with_warehouse: ['warehouse_name'],
  with_cleaners: ['cleaners_name'],
  with_repackagers: ['repackagers_name'],
};

// ─── Main Component ───────────────────────────────────────────────────────────

const DressTrackingPage = () => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState('catalogue');
  /** @type {[Dress[], Function]} */
  const [dresses, setDresses] = useState([]);
  /** @type {[HubManager[], Function]} */
  const [hubManagers, setHubManagers] = useState([]);
  /** @type {[Influencer[], Function]} */
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDressModal, setShowCreateDressModal] = useState(false);
  const [showCreateHubModal, setShowCreateHubModal] = useState(false);
  const [showCirculationModal, setShowCirculationModal] = useState(false);
  const [selectedDress, setSelectedDress] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [dressData, hubData, influencerData] = await Promise.all([
        dressService.getDresses(),
        dressService.getHubManagers({ limit: 200 }),
        influencerService.getInfluencers({ limit: 200 }),
      ]);
      setDresses(Array.isArray(dressData) ? dressData : []);
      setHubManagers(hubData.items);
      setInfluencers(influencerData.items);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Derive active shipments (circulations not with_hub, retired, or returned_to_hub)
  const activeShipments = dresses.flatMap((dress) =>
    (dress.circulations || [])
      .filter((c) => !['with_hub', 'retired', 'returned_to_hub'].includes(c.status))
      .map((c) => ({ ...c, dress_name: dress.name, dress_sku: dress.sku })),
  );

  const filteredDresses = dresses.filter(
    (d) =>
      !searchQuery ||
      d.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.sku?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const tabs = [
    { key: 'catalogue', label: 'Dress Catalogue', count: dresses.length },
    { key: 'shipments', label: 'Active Shipments', count: activeShipments.length },
    { key: 'hubs', label: 'Hub Managers', count: hubManagers.length },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Dress Tracking</h1>
          <p className="text-muted-foreground mt-1">Manage dress inventory, shipments, and hub managers</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-3 border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          {activeTab === 'catalogue' && (
            <>
              <button
                onClick={() => setShowCirculationModal(true)}
                className="flex items-center gap-2 px-4 py-3 border border-input rounded-lg hover:bg-muted/50"
              >
                <Truck size={20} />
                New Circulation
              </button>
              <button
                onClick={() => setShowCreateDressModal(true)}
                className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary/90 transition"
              >
                <Plus size={20} />
                Add Dress
              </button>
            </>
          )}
          {activeTab === 'hubs' && (
            <button
              onClick={() => setShowCreateHubModal(true)}
              className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary/90 transition"
            >
              <Plus size={20} />
              Add Hub Manager
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted rounded-lg p-1 mb-6 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-shrink-0 whitespace-nowrap flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition ${
              activeTab === tab.key
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
            <span className="ml-2 text-xs bg-accent text-muted-foreground px-2 py-0.5 rounded-full">{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading ? (
        <LoadingScreen />
      ) : (
        <>
          {/* Tab 1: Dress Catalogue */}
          {activeTab === 'catalogue' && (
            <DressCatalogueTab
              dresses={filteredDresses}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              onSelectDress={setSelectedDress}
              onCreateDress={() => setShowCreateDressModal(true)}
            />
          )}

          {/* Tab 2: Active Shipments */}
          {activeTab === 'shipments' && (
            <ActiveShipmentsTab
              shipments={activeShipments}
              onStatusUpdate={async (id, data) => {
                try {
                  await dressService.updateCirculationStatus(id, data);
                  showToast('Status updated', 'success');
                  fetchData();
                } catch (error) {
                  showToast(error.message, 'error');
                }
              }}
            />
          )}

          {/* Tab 3: Hub Managers */}
          {activeTab === 'hubs' && (
            <HubManagersTab hubManagers={hubManagers} onCreateHub={() => setShowCreateHubModal(true)} />
          )}
        </>
      )}

      {/* Detail Drawer */}
      {selectedDress && (
        <DressDetailDrawer dress={selectedDress} onClose={() => setSelectedDress(null)} onRefresh={fetchData} />
      )}

      {/* Create Dress Modal */}
      {showCreateDressModal && (
        <CreateDressModal
          onClose={() => setShowCreateDressModal(false)}
          onSuccess={() => {
            setShowCreateDressModal(false);
            fetchData();
          }}
        />
      )}

      {/* Create Hub Manager Modal */}
      {showCreateHubModal && (
        <CreateHubManagerModal
          onClose={() => setShowCreateHubModal(false)}
          onSuccess={() => {
            setShowCreateHubModal(false);
            fetchData();
          }}
        />
      )}

      {/* New Circulation Modal */}
      {showCirculationModal && (
        <NewCirculationModal
          dresses={dresses}
          hubManagers={hubManagers}
          influencers={influencers}
          onClose={() => setShowCirculationModal(false)}
          onSuccess={() => {
            setShowCirculationModal(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

// ─── Dress Catalogue Tab ──────────────────────────────────────────────────────

const DressCatalogueTab = ({ dresses, searchQuery, setSearchQuery, onSelectDress, onCreateDress }) => {
  return (
    <>
      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70" size={20} />
          <input
            type="text"
            placeholder="Search by name or SKU..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10 w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70 hover:text-foreground"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {dresses.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-12 text-center">
          <Package className="mx-auto text-muted-foreground/70 mb-4" size={48} />
          <h3 className="text-xl font-semibold text-foreground mb-2">No dresses found</h3>
          <p className="text-muted-foreground mb-6">Get started by adding your first dress</p>
          <button
            onClick={onCreateDress}
            className="bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/90 transition"
          >
            Add Dress
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {dresses.map((dress) => (
            <div
              key={dress.id}
              onClick={() => onSelectDress(dress)}
              className="bg-card rounded-lg shadow hover:shadow-md transition cursor-pointer overflow-hidden"
            >
              {/* Thumbnail — prefer product image, fall back to dress photos */}
              <div className="h-48 bg-muted flex items-center justify-center overflow-hidden">
                {dress.product_image ? (
                  <img src={dress.product_image} alt={dress.name} className="w-full h-full object-cover" />
                ) : dress.photos && dress.photos.length > 0 ? (
                  <img
                    src={dress.photos[0]?.url || dress.photos[0]}
                    alt={dress.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Package className="text-muted-foreground/50" size={48} />
                )}
              </div>

              {/* Info */}
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">{dress.product_name || dress.name}</h3>
                    <p className="text-xs text-muted-foreground">SKU: {dress.sku}</p>
                    {dress.product_price && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {'\u20B9'}
                        {Number(dress.product_price).toLocaleString('en-IN')}
                      </p>
                    )}
                  </div>
                  <ChevronRight size={16} className="text-muted-foreground/70 mt-1" />
                </div>
                <div className="flex items-center justify-between mt-3">
                  <StatusBadge value={dress.current_status || 'with_hub'} />
                  {dress.current_holder_name && (
                    <span className="text-xs text-muted-foreground truncate ml-2">{dress.current_holder_name}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// ─── Active Shipments Tab ─────────────────────────────────────────────────────

const ShipmentRow = ({ shipment, onStatusUpdate }) => {
  const [pendingStatus, setPendingStatus] = useState(null);
  const [extraFields, setExtraFields] = useState({});
  const [saving, setSaving] = useState(false);

  const handleStatusChange = (newStatus) => {
    const needed = STATUS_EXTRA_FIELDS[newStatus];
    if (needed) {
      setPendingStatus(newStatus);
      setExtraFields({});
    } else {
      onStatusUpdate(shipment.id, { status: newStatus });
    }
  };

  const confirmStatusChange = async () => {
    setSaving(true);
    await onStatusUpdate(shipment.id, { status: pendingStatus, ...extraFields });
    setSaving(false);
    setPendingStatus(null);
    setExtraFields({});
  };

  const thirdParty = getThirdPartyName(shipment);
  const nextStep = shipment.next_influencer_name || shipment.next_hub_manager_name || null;

  return (
    <>
      <tr className="hover:bg-muted/50">
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="text-sm font-medium text-foreground">{shipment.dress_name}</div>
          <div className="text-xs text-muted-foreground">{shipment.dress_sku}</div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground/80">
          {shipment.influencer_name || shipment.hub_manager_name || '\u2014'}
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <select
            value={pendingStatus || shipment.status}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="text-sm border border-input rounded-lg px-2 py-1 focus:ring-2 focus:ring-foreground focus:border-transparent"
          >
            {Object.entries(CIRCULATION_STATUS).map(([value, { label }]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-sm">
          <DueDateLabel shipment={shipment} />
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-sm text-muted-foreground">{thirdParty || '\u2014'}</td>
        <td className="px-4 py-3 whitespace-nowrap text-sm text-muted-foreground">{nextStep || '\u2014'}</td>
        <td className="px-4 py-3 whitespace-nowrap text-sm text-muted-foreground">
          {daysBetween(shipment.shipped_date)} days
        </td>
      </tr>
      {/* Inline extra fields for status transition */}
      {pendingStatus && STATUS_EXTRA_FIELDS[pendingStatus] && (
        <tr className="bg-muted/50">
          <td colSpan={7} className="px-4 py-3">
            <div className="flex items-end gap-3 flex-wrap">
              <span className="text-xs font-medium text-muted-foreground self-center">
                Updating to {CIRCULATION_STATUS[pendingStatus]?.label}:
              </span>
              {STATUS_EXTRA_FIELDS[pendingStatus].map((field) => (
                <div key={field}>
                  <label className="block text-xs text-muted-foreground mb-0.5">
                    {field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                  </label>
                  <input
                    type="text"
                    value={extraFields[field] || ''}
                    onChange={(e) => setExtraFields((prev) => ({ ...prev, [field]: e.target.value }))}
                    className="px-2 py-1 text-sm border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
                    placeholder={field.replace(/_/g, ' ')}
                  />
                </div>
              ))}
              <button
                onClick={confirmStatusChange}
                disabled={saving}
                className="px-3 py-1 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Confirm'}
              </button>
              <button
                onClick={() => {
                  setPendingStatus(null);
                  setExtraFields({});
                }}
                className="px-3 py-1 text-sm border border-input rounded-lg hover:bg-accent transition"
              >
                Cancel
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const ActiveShipmentsTab = ({ shipments, onStatusUpdate }) => {
  if (shipments.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow p-12 text-center">
        <Truck className="mx-auto text-muted-foreground/70 mb-4" size={48} />
        <h3 className="text-xl font-semibold text-foreground mb-2">No active shipments</h3>
        <p className="text-muted-foreground">All dresses are currently at their hubs</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Dress
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Current Holder
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Due Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Third Party
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Next Step
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Days in Transit
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {shipments.map((shipment) => (
              <ShipmentRow key={shipment.id} shipment={shipment} onStatusUpdate={onStatusUpdate} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Hub Managers Tab ─────────────────────────────────────────────────────────

const HubManagersTab = ({ hubManagers, onCreateHub }) => {
  if (hubManagers.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow p-12 text-center">
        <MapPin className="mx-auto text-muted-foreground/70 mb-4" size={48} />
        <h3 className="text-xl font-semibold text-foreground mb-2">No hub managers</h3>
        <p className="text-muted-foreground mb-6">Add a hub manager to start tracking dress locations</p>
        <button
          onClick={onCreateHub}
          className="bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/90 transition"
        >
          Add Hub Manager
        </button>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Phone
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Dresses Held
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {hubManagers.map((hub) => (
              <tr key={hub.id} className="hover:bg-muted/50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">{hub.name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                  {[hub.city, hub.country].filter(Boolean).join(', ') || '\u2014'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{hub.email || '\u2014'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{hub.phone || '\u2014'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{hub.dresses_held ?? 0}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {hub.is_active !== false ? (
                    <span className="px-2 py-1 text-xs rounded-full font-medium bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">
                      Active
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs rounded-full font-medium bg-muted text-muted-foreground">
                      Inactive
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Dress Detail Drawer ──────────────────────────────────────────────────────

const DressDetailDrawer = ({ dress, onClose, onRefresh }) => {
  const [timeline, setTimeline] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(true);

  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        setLoadingTimeline(true);
        const data = await dressService.getDressTimeline(dress.id);
        setTimeline(Array.isArray(data) ? data : []);
      } catch (error) {
        // Timeline may not exist yet for new dresses
        setTimeline([]);
      } finally {
        setLoadingTimeline(false);
      }
    };
    fetchTimeline();
  }, [dress.id]);

  return (
    <>
      <ModalDrawer onClose={onClose} maxWidth="lg" className="shadow-2xl">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
            <div>
              <h2 className="text-xl font-bold text-foreground truncate">{dress.product_name || dress.name}</h2>
              <p className="text-sm text-muted-foreground">SKU: {dress.sku}</p>
              {dress.product_price && (
                <p className="text-sm text-muted-foreground">
                  {'\u20B9'}
                  {Number(dress.product_price).toLocaleString('en-IN')}
                </p>
              )}
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
              <X size={24} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Product Image */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground/80 uppercase tracking-wider">Product Image</h3>
              {dress.product_image ? (
                <img
                  src={dress.product_image}
                  alt={dress.product_name || dress.name}
                  className="w-full max-w-xs h-48 object-cover rounded-lg"
                />
              ) : dress.photos && dress.photos.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {dress.photos.map((photo, idx) => (
                    <img
                      key={idx}
                      src={photo?.url || photo}
                      alt={`${dress.name} ${idx + 1}`}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground/70 italic py-4 text-center border border-dashed border-border rounded-lg">
                  No image available
                </div>
              )}
            </div>

            {/* Description */}
            {dress.description && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground/80 uppercase tracking-wider">Description</h3>
                <p className="text-sm text-muted-foreground">{dress.description}</p>
              </div>
            )}

            {/* Current Status */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground/80 uppercase tracking-wider">Current Status</h3>
              <div className="flex items-center gap-3">
                <StatusBadge value={dress.current_status || 'with_hub'} />
                {dress.current_holder_name && (
                  <span className="text-sm text-muted-foreground">{dress.current_holder_name}</span>
                )}
              </div>
            </div>

            {/* Circulation Timeline */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground/80 uppercase tracking-wider">
                Circulation Timeline
              </h3>
              {loadingTimeline ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-foreground"></div>
                </div>
              ) : timeline.length === 0 ? (
                <div className="text-sm text-muted-foreground/70 italic py-4 text-center border border-dashed border-border rounded-lg">
                  No circulation history yet
                </div>
              ) : (
                <div className="space-y-4">
                  {timeline.map((entry, idx) => {
                    const entryThirdParty = getThirdPartyName(entry);
                    const dueDateField = getDueDateField(entry.status);
                    const dueDate = dueDateField ? entry[dueDateField] : null;
                    const entryNextStep = entry.next_influencer_name || entry.next_hub_manager_name || null;

                    return (
                      <div key={entry.id || idx} className="relative pl-6 border-l-2 border-border pb-4 last:pb-0">
                        <div className="absolute -left-[7px] top-0 w-3 h-3 rounded-full bg-muted-foreground/50 border-2 border-white"></div>
                        <div className="flex items-center gap-2 mb-1">
                          <StatusBadge value={entry.status} />
                          {entryThirdParty && (
                            <span className="text-xs text-muted-foreground font-medium">{entryThirdParty}</span>
                          )}
                          <span className="text-xs text-muted-foreground/70">#{entry.sequence || idx + 1}</span>
                        </div>
                        <p className="text-sm text-foreground/80">
                          {entry.influencer_name && <span className="font-medium">{entry.influencer_name}</span>}
                          {entry.hub_manager_name && (
                            <span className="text-muted-foreground"> via {entry.hub_manager_name}</span>
                          )}
                        </p>
                        {/* Due date for current step */}
                        {dueDate && (
                          <p className="text-xs mt-1">
                            <span className="text-muted-foreground">Due: </span>
                            <DueDateLabel shipment={entry} />
                            <span className="text-muted-foreground/70 ml-1">
                              ({new Date(dueDate).toLocaleDateString()})
                            </span>
                          </p>
                        )}
                        {/* Next step info */}
                        {entryNextStep && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Next: <span className="font-medium text-foreground/80">{entryNextStep}</span>
                          </p>
                        )}
                        {entry.shipped_date && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Shipped: {new Date(entry.shipped_date).toLocaleDateString()}
                            {entry.returned_date && (
                              <> | Returned: {new Date(entry.returned_date).toLocaleDateString()}</>
                            )}
                          </p>
                        )}
                        {entry.notes && <p className="text-xs text-muted-foreground mt-1 italic">{entry.notes}</p>}
                        {/* Content links */}
                        {entry.content_links && entry.content_links.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {entry.content_links.map((link, linkIdx) => (
                              <a
                                key={linkIdx}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
                              >
                                {link.label || `Content ${linkIdx + 1}`}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </ModalDrawer>
    </>
  );
};

// ─── Create Dress Modal ───────────────────────────────────────────────────────

const CreateDressModal = ({ onClose, onSuccess }) => {
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productSearch, setProductSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [description, setDescription] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  // X and Cancel dismiss the modal — confirm first if there's anything to
  // lose. A successful submit calls onSuccess()/onClose() directly, bypassing this.
  const handleCloseClick = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    onClose();
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const data = await adminProductService.getAllProducts({ limit: 200 });
        setProducts(data.items);
      } catch (error) {
        showToast('Failed to load products', 'error');
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, [showToast]);

  const filteredProducts = products.filter(
    (p) =>
      !productSearch ||
      p.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
      p.slug?.toLowerCase().includes(productSearch.toLowerCase()),
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProduct) {
      showToast('Please select a product', 'error');
      return;
    }
    setSaving(true);
    try {
      await dressService.createDress({
        product_id: selectedProduct.id,
        description: description || undefined,
      });
      showToast('Dress added successfully', 'success');
      onSuccess();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal maxWidth="2xl" closeOnBackdrop={false} hasUnsavedChanges={isDirty}>
      <div className="sticky top-0 bg-card border-b px-6 py-4 flex justify-between items-center">
        <h2 className="text-2xl font-bold">Add Dress from Product</h2>
        <button onClick={handleCloseClick} className="text-muted-foreground hover:text-foreground">
          <X size={24} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        {/* Product Selector */}
        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-1">Select Product *</label>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70" size={16} />
            <input
              type="text"
              placeholder="Search products..."
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              className="pl-9 w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent text-sm"
            />
          </div>

          {loadingProducts ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-foreground"></div>
            </div>
          ) : (
            <div className="border border-border rounded-lg max-h-60 overflow-y-auto">
              {filteredProducts.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground text-center">No products found</div>
              ) : (
                filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    onClick={() => {
                      setSelectedProduct(product);
                      setIsDirty(true);
                    }}
                    className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50 border-b border-border/60 last:border-b-0 ${
                      selectedProduct?.id === product.id
                        ? 'bg-blue-50 dark:bg-blue-950/50 border-blue-200 dark:border-blue-900'
                        : ''
                    }`}
                  >
                    <div className="w-12 h-12 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                      {product.image_urls?.[0] ? (
                        <img src={product.image_urls[0]} alt={product.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package size={16} className="text-muted-foreground/50" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{product.name}</p>
                      <p className="text-xs text-muted-foreground">{product.slug}</p>
                    </div>
                    <div className="text-sm text-foreground/80 font-medium">
                      {product.price ? `\u20B9${Number(product.price).toLocaleString('en-IN')}` : ''}
                    </div>
                    {selectedProduct?.id === product.id && (
                      <span className="text-blue-600 dark:text-blue-400 text-xs font-medium">Selected</span>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Selected Product Preview */}
        {selectedProduct && (
          <div className="bg-green-50 dark:bg-green-950/50 border border-green-200 dark:border-green-900 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                {selectedProduct.image_urls?.[0] ? (
                  <img
                    src={selectedProduct.image_urls[0]}
                    alt={selectedProduct.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package size={20} className="text-muted-foreground/50" />
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">{selectedProduct.name}</p>
                <p className="text-xs text-muted-foreground">SKU: {selectedProduct.slug}</p>
                {selectedProduct.price && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Price: {'\u20B9'}
                    {Number(selectedProduct.price).toLocaleString('en-IN')}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-1">Tracking Notes (optional)</label>
          <textarea
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              setIsDirty(true);
            }}
            rows={3}
            placeholder="e.g. Pulled for summer campaign shoot"
            className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent resize-none"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={handleCloseClick}
            className="px-6 py-2 border border-input rounded-lg hover:bg-muted/50 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !selectedProduct}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Add Dress'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ─── Create Hub Manager Modal ─────────────────────────────────────────────────

const CreateHubManagerModal = ({ onClose, onSuccess }) => {
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    phone_country_code: '',
    country: '',
    city: '',
  });
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  // X and Cancel dismiss the modal — confirm first if there's anything to
  // lose. A successful submit calls onSuccess() directly, bypassing this.
  const handleCloseClick = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    setSaving(true);
    try {
      await dressService.createHubManager(formData);
      showToast('Hub manager added successfully', 'success');
      onSuccess();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal maxWidth="lg" closeOnBackdrop={false} hasUnsavedChanges={isDirty}>
      <div className="sticky top-0 bg-card border-b px-6 py-4 flex justify-between items-center">
        <h2 className="text-2xl font-bold">Add Hub Manager</h2>
        <button onClick={handleCloseClick} className="text-muted-foreground hover:text-foreground">
          <X size={24} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-1">Name *</label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Country</label>
            <input
              type="text"
              value={formData.country}
              onChange={(e) => handleChange('country', e.target.value)}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">City</label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-1">Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-1">Phone</label>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="+1"
              value={formData.phone_country_code}
              onChange={(e) => handleChange('phone_country_code', e.target.value)}
              className="w-20 px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent text-sm"
            />
            <input
              type="text"
              placeholder="Phone number"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              className="flex-1 px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={handleCloseClick}
            className="px-6 py-2 border border-input rounded-lg hover:bg-muted/50 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Add Hub Manager'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ─── New Circulation Modal ────────────────────────────────────────────────────

const NewCirculationModal = ({ dresses, hubManagers, influencers, onClose, onSuccess }) => {
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [formData, setFormDataRaw] = useState({
    dress_id: '',
    hub_manager_id: '',
    influencer_id: '',
    expected_return_date: '',
    notes: '',
    status: 'shipped_to_influencer',
    shipping_company: '',
    tracking_number: '',
    due_at_warehouse: '',
    due_at_influencer: '',
    due_at_cleaners: '',
    due_at_repackagers: '',
    due_at_hub: '',
    next_influencer_id: '',
    next_hub_manager_id: '',
  });
  const setFormData = useCallback((updater) => {
    setIsDirty(true);
    setFormDataRaw(updater);
  }, []);

  // X and Cancel dismiss the modal — confirm first if there's anything to
  // lose. A successful submit calls onSuccess() directly, bypassing this.
  const handleCloseClick = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.dress_id || !formData.hub_manager_id || !formData.influencer_id) {
      showToast('Dress, hub manager, and influencer are required', 'error');
      return;
    }
    setSaving(true);
    try {
      await dressService.createCirculation(formData);
      showToast('Circulation created successfully', 'success');
      onSuccess();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal maxWidth="2xl" closeOnBackdrop={false} hasUnsavedChanges={isDirty}>
      <div className="sticky top-0 bg-card border-b px-6 py-4 flex justify-between items-center">
        <h2 className="text-2xl font-bold">New Circulation</h2>
        <button onClick={handleCloseClick} className="text-muted-foreground hover:text-foreground">
          <X size={24} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Dress *</label>
            <select
              required
              value={formData.dress_id}
              onChange={(e) => setFormData({ ...formData, dress_id: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            >
              <option value="">Select a dress</option>
              {dresses.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} ({d.sku})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Initial Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            >
              {Object.entries(CIRCULATION_STATUS).map(([value, { label }]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-1">Source Hub Manager *</label>
          <select
            required
            value={formData.hub_manager_id}
            onChange={(e) => setFormData({ ...formData, hub_manager_id: e.target.value })}
            className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
          >
            <option value="">Select hub manager</option>
            {hubManagers.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name} ({[h.city, h.country].filter(Boolean).join(', ')})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-1">Destination Influencer *</label>
          <select
            required
            value={formData.influencer_id}
            onChange={(e) => setFormData({ ...formData, influencer_id: e.target.value })}
            className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
          >
            <option value="">Select influencer</option>
            {influencers.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name} {i.ig_username ? `(@${i.ig_username})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Shipping Details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Shipping Company</label>
            <input
              type="text"
              value={formData.shipping_company}
              onChange={(e) => setFormData({ ...formData, shipping_company: e.target.value })}
              placeholder="e.g. DHL, FedEx"
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Tracking Number</label>
            <input
              type="text"
              value={formData.tracking_number}
              onChange={(e) => setFormData({ ...formData, tracking_number: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>
        </div>

        {/* Next Step */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Next Influencer</label>
            <select
              value={formData.next_influencer_id}
              onChange={(e) => setFormData({ ...formData, next_influencer_id: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            >
              <option value="">None</option>
              {influencers.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name} {i.ig_username ? `(@${i.ig_username})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Next Hub Manager</label>
            <select
              value={formData.next_hub_manager_id}
              onChange={(e) => setFormData({ ...formData, next_hub_manager_id: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            >
              <option value="">None</option>
              {hubManagers.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name} ({[h.city, h.country].filter(Boolean).join(', ')})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Due Dates */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground/80">Expected Due Dates</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { key: 'due_at_warehouse', label: 'At Warehouse' },
              { key: 'due_at_influencer', label: 'At Influencer' },
              { key: 'due_at_cleaners', label: 'At Cleaners' },
              { key: 'due_at_repackagers', label: 'At Repackagers' },
              { key: 'due_at_hub', label: 'Back at Hub' },
            ].map(({ key, label }) => (
              <div key={key}>
                <label className="block text-xs text-muted-foreground mb-0.5">{label}</label>
                <input
                  type="datetime-local"
                  value={formData[key]}
                  onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                  className="w-full px-3 py-1.5 text-sm border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
                />
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-1">Expected Return Date</label>
          <input
            type="date"
            value={formData.expected_return_date}
            onChange={(e) => setFormData({ ...formData, expected_return_date: e.target.value })}
            className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-1">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
            placeholder="Any special instructions..."
            className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent resize-none"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={handleCloseClick}
            className="px-6 py-2 border border-input rounded-lg hover:bg-muted/50 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition disabled:opacity-50"
          >
            {saving ? 'Creating...' : 'Create Circulation'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default DressTrackingPage;
